/**
 * Markdown Renderer Component
 *
 * Renders Markdown content with support for:
 * - GitHub Flavored Markdown (tables, task lists, etc.)
 * - Mathematical equations (KaTeX)
 * - Code syntax highlighting
 * - Custom styling for EnergyPlus documentation
 * - HTML figures with image placeholders (from LaTeX conversion)
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import { cn } from '@/lib/utils';
import { IDFCodeBlock, looksLikeIDF } from '../components/IDFCodeBlock';

// Use permissive types for react-markdown components to avoid strict type conflicts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ComponentProps = any;

// KaTeX CSS is loaded globally in index.html or via import
import 'katex/dist/katex.min.css';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  onObjectClick?: (objectType: string) => void;
}

// Pattern to match EnergyPlus object names (Word:Word or Word:Word:Word, etc.)
const OBJECT_NAME_PATTERN = /\b([A-Z][a-zA-Z0-9]*(?::[A-Z][a-zA-Z0-9]*)+)\b/g;

/**
 * Generate a URL-friendly slug from text
 */
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Extract text content from React children
 */
function extractTextFromChildren(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join('');
  }
  if (React.isValidElement(children) && children.props?.children) {
    return extractTextFromChildren(children.props.children);
  }
  return '';
}

/**
 * TOC entry type
 */
export interface TocEntry {
  id: string;
  text: string;
  level: number;
}

/**
 * Extract table of contents from markdown content
 */
export function extractToc(content: string): TocEntry[] {
  const headingRegex = /^(#{1,3})\s+(.+)$/gm;
  const entries: TocEntry[] = [];
  let match;

  while ((match = headingRegex.exec(content)) !== null) {
    const hashes = match[1];
    const rawText = match[2];
    if (!hashes || !rawText) continue;

    const level = hashes.length;
    const text = rawText
      .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold
      .replace(/\*([^*]+)\*/g, '$1')     // Remove italic
      .replace(/`([^`]+)`/g, '$1')       // Remove code
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links
      .trim();
    const id = generateSlug(text);

    if (text && id) {
      entries.push({ id, text, level });
    }
  }

  return entries;
}

// Helper to render text with auto-linked object names
function renderTextWithObjectLinks(
  text: string,
  onObjectClick?: (objectType: string) => void
): React.ReactNode[] {
  if (!onObjectClick) return [text];

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Reset regex state
  OBJECT_NAME_PATTERN.lastIndex = 0;

  while ((match = OBJECT_NAME_PATTERN.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Add the linked object name
    const objectName = match[1];
    if (objectName) {
      parts.push(
        <button
          key={`${objectName}-${match.index}`}
          onClick={() => onObjectClick(objectName)}
          className="text-primary hover:underline cursor-pointer font-medium"
        >
          {objectName}
        </button>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

// Helper to process children and auto-link object names in text nodes
function processChildren(
  children: React.ReactNode,
  onObjectClick?: (objectType: string) => void
): React.ReactNode {
  if (!onObjectClick) return children;

  return React.Children.map(children, (child, index) => {
    if (typeof child === 'string') {
      const linked = renderTextWithObjectLinks(child, onObjectClick);
      return linked.length === 1 ? linked[0] : <React.Fragment key={index}>{linked}</React.Fragment>;
    }
    return child;
  });
}

export function MarkdownRenderer({ content, className, onObjectClick }: MarkdownRendererProps) {
  return (
    <div
      className={cn(
        'prose prose-sm dark:prose-invert max-w-none',
        'prose-headings:font-semibold prose-headings:text-foreground',
        'prose-p:text-foreground/90',
        'prose-a:text-primary prose-a:no-underline hover:prose-a:underline',
        'prose-code:text-primary prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm',
        'prose-pre:bg-muted prose-pre:border prose-pre:border-border',
        'prose-table:border prose-table:border-border',
        'prose-th:bg-muted prose-th:p-2 prose-th:text-left prose-th:font-medium',
        'prose-td:p-2 prose-td:border-t prose-td:border-border',
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkMath, remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
        components={{
          // Custom figure rendering for HTML figures from LaTeX conversion
          figure({ children, id, ...props }: ComponentProps & { id?: string }) {
            return (
              <figure
                id={id}
                className="docs-figure my-6 mx-auto max-w-full"
                {...props}
              >
                {children}
              </figure>
            );
          },

          // Handle figcaption
          figcaption({ children }: ComponentProps) {
            return (
              <figcaption className="docs-figcaption text-center text-sm text-muted-foreground mt-2 px-4">
                {children}
              </figcaption>
            );
          },

          // Handle image placeholder spans from LaTeX conversion
          span({ className, node, ...props }: ComponentProps) {
            // Check if this is an image placeholder from LaTeX conversion
            if (className === 'image placeholder' || className?.includes('image')) {
              // Access data attributes from the node properties
              const nodeProps = node?.properties as Record<string, unknown> | undefined;
              const src = (nodeProps?.['dataOriginalImageSrc'] || nodeProps?.['data-original-image-src']) as string;
              const title = (nodeProps?.['dataOriginalImageTitle'] || nodeProps?.['data-original-image-title']) as string;

              if (src) {
                // Convert the path to the correct location
                const imagePath = src.startsWith('media/')
                  ? `/docs/engineering-reference/images/${src.replace('media/', '')}`
                  : src.startsWith('./images/')
                    ? `/docs/engineering-reference/images/${src.replace('./images/', '')}`
                    : src;

                return (
                  <img
                    src={imagePath}
                    alt={title || 'Figure'}
                    className="docs-figure-img max-w-full h-auto mx-auto rounded-md"
                    loading="lazy"
                  />
                );
              }
              return null;
            }

            // Return regular span for other cases
            return <span className={className} {...props} />;
          },

          // Custom image rendering with responsive styles
          img({ src, alt, ...props }: ComponentProps & { src?: string; alt?: string }) {
            // Handle relative paths for engineering reference images
            let imagePath = src || '';
            if (imagePath.startsWith('./images/')) {
              imagePath = `/docs/engineering-reference/images/${imagePath.replace('./images/', '')}`;
            } else if (imagePath.startsWith('media/')) {
              imagePath = `/docs/engineering-reference/images/${imagePath.replace('media/', '')}`;
            }

            return (
              <img
                src={imagePath}
                alt={alt || 'Figure'}
                className="docs-figure-img max-w-full h-auto mx-auto rounded-md my-4"
                loading="lazy"
                {...props}
              />
            );
          },


          // Custom code block rendering with IDF syntax highlighting
          code({ className, children, node, ...props }: ComponentProps) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';

            // Determine if this is inline code by checking if the parent is a <pre> tag
            // react-markdown wraps block code in <pre><code>, inline code is just <code>
            const isBlock = node?.position && node.position.start.line !== node.position.end.line;
            const hasNewlines = typeof children === 'string' && children.includes('\n');
            const isInline = !className && !isBlock && !hasNewlines;

            if (isInline) {
              return (
                <code className="bg-muted px-1 py-0.5 rounded text-sm text-primary" {...props}>
                  {children}
                </code>
              );
            }

            // Get the code content as string
            const codeContent = typeof children === 'string'
              ? children
              : React.Children.toArray(children)
                  .map(child => typeof child === 'string' ? child : '')
                  .join('');

            // Use IDF highlighting for IDF language or if content looks like IDF
            const isIDF = language === 'idf' || language === 'energyplus' ||
              (!language && looksLikeIDF(codeContent));

            if (isIDF) {
              return <IDFCodeBlock code={codeContent.replace(/\n$/, '')} />;
            }

            // For other code blocks
            return (
              <div className="relative group">
                {language && (
                  <span className="absolute top-2 right-2 text-xs text-muted-foreground bg-background/80 px-1.5 py-0.5 rounded">
                    {language}
                  </span>
                )}
                <pre className="bg-muted border border-border rounded-md p-4 overflow-x-auto">
                  <code className={cn('text-sm', className)} {...props}>
                    {children}
                  </code>
                </pre>
              </div>
            );
          },

          // Custom pre rendering - check if content looks like IDF
          pre({ children, ...props }: ComponentProps) {
            // Extract text content from children
            const extractText = (node: React.ReactNode): string => {
              if (typeof node === 'string') return node;
              if (typeof node === 'number') return String(node);
              if (Array.isArray(node)) return node.map(extractText).join('');
              if (React.isValidElement(node) && node.props?.children) {
                return extractText(node.props.children);
              }
              return '';
            };

            const textContent = extractText(children);

            // Check if this pre block contains IDF-like content (not already in code block)
            // This handles indented text that pandoc converts to <pre>
            if (looksLikeIDF(textContent) && !React.Children.toArray(children).some(
              child => React.isValidElement(child) && child.type === 'code'
            )) {
              return <IDFCodeBlock code={textContent.trim()} />;
            }

            // Default pre rendering
            return (
              <pre className="bg-muted border border-border rounded-md p-4 overflow-x-auto" {...props}>
                {children}
              </pre>
            );
          },

          // Custom table styling
          table({ children }: ComponentProps) {
            return (
              <div className="overflow-x-auto my-4">
                <table className="min-w-full divide-y divide-border border border-border rounded-md">
                  {children}
                </table>
              </div>
            );
          },

          thead({ children }: ComponentProps) {
            return <thead className="bg-muted">{children}</thead>;
          },

          th({ children }: ComponentProps) {
            return (
              <th className="px-3 py-2 text-left text-sm font-medium text-foreground">
                {children}
              </th>
            );
          },

          td({ children }: ComponentProps) {
            return (
              <td className="px-3 py-2 text-sm text-foreground/90 border-t border-border">
                {children}
              </td>
            );
          },

          // Custom blockquote for notes/warnings
          blockquote({ children }: ComponentProps) {
            return (
              <blockquote className="border-l-4 border-primary/50 pl-4 py-2 my-4 bg-primary/5 rounded-r-md">
                {children}
              </blockquote>
            );
          },

          // Custom heading anchors with IDs for TOC navigation
          h1({ children, ...props }: ComponentProps) {
            const text = extractTextFromChildren(children);
            const id = generateSlug(text);
            return (
              <h1 id={id} className="text-2xl font-bold text-foreground mt-6 mb-4 scroll-mt-4" {...props}>
                {children}
              </h1>
            );
          },

          h2({ children, ...props }: ComponentProps) {
            const text = extractTextFromChildren(children);
            const id = generateSlug(text);
            return (
              <h2 id={id} className="text-xl font-semibold text-foreground mt-6 mb-3 pb-2 border-b border-border scroll-mt-4" {...props}>
                {children}
              </h2>
            );
          },

          h3({ children, ...props }: ComponentProps) {
            const text = extractTextFromChildren(children);
            const id = generateSlug(text);
            return (
              <h3 id={id} className="text-lg font-semibold text-foreground mt-4 mb-2 scroll-mt-4" {...props}>
                {children}
              </h3>
            );
          },

          // Links
          a({ href, children, ...props }: ComponentProps & { href?: string }) {
            const isExternal = href?.startsWith('http');
            return (
              <a
                href={href}
                className="text-primary hover:underline"
                {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                {...props}
              >
                {children}
                {isExternal && (
                  <svg
                    className="inline-block w-3 h-3 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                )}
              </a>
            );
          },

          // Paragraphs with auto-linked object names
          p({ children }: ComponentProps) {
            // Process children to auto-link object names
            const processedChildren = processChildren(children, onObjectClick);
            return <p className="my-2">{processedChildren}</p>;
          },

          // Lists - use list-outside with proper padding for correct rendering
          ul({ children }: ComponentProps) {
            return <ul className="list-disc list-outside pl-6 space-y-1 my-2">{children}</ul>;
          },

          ol({ children }: ComponentProps) {
            return <ol className="list-decimal list-outside pl-6 space-y-1 my-2">{children}</ol>;
          },

          li({ children }: ComponentProps) {
            const processedChildren = processChildren(children, onObjectClick);
            return <li className="text-foreground/90 pl-1">{processedChildren}</li>;
          },

          // Horizontal rule
          hr() {
            return <hr className="my-6 border-border" />;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Render a simple field memo (no math support needed)
 */
export function SimpleMemoRenderer({ content, className }: { content: string; className?: string }) {
  // Split by newlines and render as paragraphs
  const paragraphs = content.split('\n').filter((p) => p.trim());

  return (
    <div className={cn('text-sm text-foreground/80 space-y-2', className)}>
      {paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
}
